import React, { useState } from "react";

import {
  ExperienceListDto,
  HistoryDto,
  OrganizationDto,
} from "@intelligems/ig-types";
import { getHistory } from "@src/api/histories/get-history";
import { historyKeys } from "@src/api/queryKeys";
import { useBooleanWebUiScopeMap } from "@src/components/LimitAccess/check-scopes-hooks";
import {
  useCheckFreeTrialStatus,
  useCheckSubscriptionStatus,
} from "@src/components/LimitAccess/check-subscriptions-hooks";
import {
  useIsCartTransformTestConflictActive,
  useIsPriceTestIntegrationReady,
} from "@src/components/LimitAccess/experiment-scope-hooks";
import { ScopedStartExperienceWithDisabled } from "@src/components/LimitAccess/ScopedStartExperienceWithDisabled";
import { useDialogs } from "@src/hooks/dialog";
import { Experience } from "@src/store/slices/experiences/types";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";

import { ActionExperienceButton } from "./ActionExperienceButton";

interface StartExperienceButtonProps {
  rowExperience: Experience;
  currentExperience?: Experience;
  experiencesList: ExperienceListDto[];
  organization: OrganizationDto;
  renderLocation: "overview" | "experience";
  onStartButtonClick: (
    rowExperience: Experience,
    history: HistoryDto[],
    isSubscriptionStartActive?: boolean
  ) => void;
  hasSchedule: boolean;
}

export const StartExperienceButton = ({
  experiencesList,
  rowExperience,
  currentExperience,
  onStartButtonClick,
  renderLocation,
  hasSchedule,
}: StartExperienceButtonProps) => {
  const { openDialog } = useDialogs();
  const queryClient = useQueryClient();

  // History is only needed to drive the start-confirmation flow on click, so we
  // fetch it on demand instead of per row. Eagerly querying it for every row is
  // what made the virtualized experiments list jank while scrolling. fetchQuery
  // returns the cached value instantly when it is already fresh (24h staleTime).
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);

  const handleStartClick = async () => {
    setIsFetchingHistory(true);
    let history: HistoryDto[] = [];
    try {
      const data = await queryClient.fetchQuery({
        queryKey: historyKeys.one(rowExperience.id!),
        queryFn: () =>
          getHistory({
            body: undefined,
            pathParams: { experienceId: rowExperience.id! },
            queryParams: undefined,
          }),
      });
      history = data?.history ?? [];
    } catch {
      // Mirror useGetHistory's `onError: ignore` — fall back to empty history.
    } finally {
      setIsFetchingHistory(false);
    }
    onStartButtonClick(rowExperience, history, isSubscriptionActive || false);
  };

  const isStartExperimentMutating = useIsMutating({
    mutationKey: ["startExperience"],
  });
  const isEndExperimentMutating = useIsMutating({
    mutationKey: ["endExperience"],
  });
  const isPauseExperimentMutating = useIsMutating({
    mutationKey: ["pauseExperience"],
  });

  const isSubscriptionActive = useCheckSubscriptionStatus();
  const isFreeTrialActive = useCheckFreeTrialStatus();

  const isShippingAlreadyActive: ExperienceListDto[] = experiencesList
    .filter((experience) => experience.status === "started")
    .filter((experience) => experience.testTypes.hasTestShipping);
  const isPriceTestIntegrationReady = useIsPriceTestIntegrationReady();
  const scopeMap = useBooleanWebUiScopeMap();
  // Check against the org's full experiment list (not the `experiencesList`
  // prop), which on this component's actual pages is scoped to a single
  // module (Pricing page only has pricing rows, Shipping page only has
  // shipping rows) and can never contain the opposite-type experience this
  // check needs to see
  const hasPricingCartTransformConflict = useIsCartTransformTestConflictActive(
    rowExperience.id,
    "hasTestPricing",
    Boolean(rowExperience.testTypes.hasTestPricing)
  );
  const hasShippingCartTransformConflict = useIsCartTransformTestConflictActive(
    rowExperience.id,
    "hasTestShipping",
    Boolean(rowExperience.testTypes.hasTestShipping)
  );
  const hasCartTransformConflict =
    rowExperience.status !== "started" &&
    ((rowExperience.testTypes.hasTestPricing &&
      hasPricingCartTransformConflict) ||
      (rowExperience.testTypes.hasTestShipping &&
        hasShippingCartTransformConflict));

  return (
    <ScopedStartExperienceWithDisabled
      experience={rowExperience}
      debugId={"experiment-list-button"}
      adminDisable={
        (rowExperience.testTypes.hasTestShipping &&
          rowExperience.status !== "started" &&
          isShippingAlreadyActive.length > 0) ||
        hasCartTransformConflict
      }
    >
      <ActionExperienceButton
        renderLocation={renderLocation}
        isLoading={
          isFetchingHistory ||
          (Boolean(
            isStartExperimentMutating ||
            isEndExperimentMutating ||
            isPauseExperimentMutating
          ) &&
            currentExperience?.id === rowExperience.id)
        }
        onClick={() => {
          void handleStartClick();
        }}
        action={"start"}
        experience={rowExperience}
        hasSchedule={hasSchedule}
        onDisableClick={() => {
          if (
            rowExperience.type === "pricing" &&
            !isPriceTestIntegrationReady &&
            scopeMap["start_price_experiment"] &&
            isSubscriptionActive
          ) {
            openDialog("completePricingIntegration", rowExperience);
          } else if (isFreeTrialActive) {
            openDialog("freeTrial", { isPersonalization: false });
          } else if (!isSubscriptionActive) {
            openDialog("billingStatus", rowExperience);
          }
        }}
      />
    </ScopedStartExperienceWithDisabled>
  );
};
